import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import type { Persona } from './api';

/**
 * piezas.tsx — la persona, como tarjeta y como fila.
 *
 * ## Por qué la tarjeta es un `<button>`
 *
 * En el vanilla era un `<div onclick>` con dos `onmouseover` que escribían
 * `style.transform` a mano. Medido en el navegador el 2026-09-11 con doce
 * personas:
 *
 *     vista Tarjetas (la de por defecto)   0 elementos alcanzables con Tab
 *     vista Lista                          36
 *
 * **Ninguna ficha se podía abrir con el teclado en la vista por defecto.** Y
 * tampoco se podía escapar: los dos selectores de vista eran `<span
 * class="chip">`, así que ni siquiera se llegaba a la vista que sí funcionaba.
 *
 * Es WCAG 2.1.1 (Teclado), nivel A — el criterio más básico que existe — en la
 * pantalla que `test_escapado.py` llama «la ruta de mayor exposición del
 * portal».
 *
 * Un `<button>` lo arregla entero: recibe foco, responde a Enter y a espacio, y
 * el lector de pantalla lo anuncia. El realce al pasar el ratón pasa a `hover:`
 * y `focus-visible:` en CSS, que además funciona para quien no usa ratón.
 */

export function TarjetaDePersona({ p, alAbrir }: { p: Persona; alAbrir: (id: number) => void }) {
  return (
    <button
      type="button"
      onClick={() => alAbrir(p.id)}
      // `text-left` porque un <button> centra su contenido por defecto y esta
      // tarjeta no es un botón de acción: es una ficha que se puede abrir.
      className="card group w-full overflow-hidden text-left transition-transform
                 hover:-translate-y-1 hover:shadow-md focus-visible:focus-anillo
                 focus-visible:-translate-y-1 motion-reduce:transition-none
                 motion-reduce:hover:translate-y-0"
      aria-label={`Ver la ficha de ${p.full_name}`}
    >
      <div className="flex h-28 items-center justify-center
                      bg-[linear-gradient(135deg,var(--brand),var(--navy-light))]">
        <Avatar
          nombre={p.full_name}
          foto={p.avatar_url}
          tamano={72}
          className="border-[3px] border-white text-7"
        />
      </div>

      <div className="card-body p-4 text-center">
        <div className="text-4 font-bold text-brand-ink">{p.full_name}</div>
        {p.position && <div className="mt-0.5 text-2 text-muted">{p.position}</div>}
        {p.city && (
          <div className="mt-0.5 text-2 text-muted">
            <i className="bi bi-geo-alt" aria-hidden="true" /> {p.city}
          </div>
        )}
        {p.birth_date_display && (
          <div className="mt-1 text-2 text-muted">
            <i className="bi bi-cake" aria-hidden="true" /> {p.birth_date_display}
          </div>
        )}
      </div>

      {/* El contacto va como TEXTO dentro del botón, no como enlaces: un `<a>`
          dentro de un `<button>` no es marcado válido y el teclado se pierde
          entre los dos. Los enlaces vivos están en la vista de lista y en la
          ficha, que es donde se va a contactar a alguien. */}
      <div className="border-t border-line bg-sunken px-4 py-2.5">
        {p.email && (
          <div className="truncate text-2 text-muted">
            <i className="bi bi-envelope text-brand-ink" aria-hidden="true" /> {p.email}
          </div>
        )}
        {p.phone && (
          <div className="truncate text-2 text-muted">
            <i className="bi bi-telephone text-brand-ink" aria-hidden="true" /> {p.phone}
          </div>
        )}
        {!p.email && !p.phone && (
          <div className="text-2 text-muted">Sin contacto registrado</div>
        )}
      </div>
    </button>
  );
}

export function FilaDePersona({ p, alAbrir }: { p: Persona; alAbrir: (id: number) => void }) {
  return (
    <tr>
      <td>
        <div className="flex items-center gap-2.5">
          <Avatar nombre={p.full_name} foto={p.avatar_url} tamano={36} />
          <div className="min-w-0">
            <div className="truncate text-3 font-semibold">{p.full_name}</div>
            {p.birth_date_display && (
              <div className="text-1 text-muted">
                <i className="bi bi-cake" aria-hidden="true" /> {p.birth_date_display}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="text-3">{p.position || '—'}</td>
      <td className="text-3">{p.city || '—'}</td>
      <td>
        {p.email ? (
          <a href={`mailto:${p.email}`} className="text-2 text-brand-ink">
            <i className="bi bi-envelope" aria-hidden="true" /> {p.email}
          </a>
        ) : '—'}
      </td>
      <td>
        {p.phone ? (
          <a href={`tel:${p.phone}`} className="text-2 text-brand-ink">
            <i className="bi bi-telephone" aria-hidden="true" /> {p.phone}
          </a>
        ) : '—'}
      </td>
      <td>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Ver la ficha de ${p.full_name}`}
          onClick={() => alAbrir(p.id)}
        >
          <i className="bi bi-person-badge" aria-hidden="true" />
        </Button>
      </td>
    </tr>
  );
}
